import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { getPostLoginPath, isPartnerRole } from "../../auth/loginNavigation";
import { otpService } from "../../services/apiService";
import { toast } from "react-toastify";
import { FaChevronDown } from "react-icons/fa";
import promoImage from "../../assets/img/create-account-office.jpeg";
import { LoginPageWrapper } from "./loginPageLayout";

const PartnerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneLogin, logout } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [localError, setLocalError] = useState("");
  const [localSuccess, setLocalSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("authPortal") !== "used") {
      localStorage.setItem("authPortal", "new");
    }
  }, []);

  const goTo = (path) => navigate(path, { state: location.state });

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneNumber(value);
    setLocalError("");
    setLocalSuccess("");
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    setLocalError("");
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLocalError("");
    setLocalSuccess("");
    if (!phoneNumber || phoneNumber.length !== 10) {
      setLocalError("Please enter a valid 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      const res = await otpService.sendOTP(phoneNumber);
      setOtpSent(true);
      const devOtp = res?.data?.devOtp;
      setLocalSuccess(
        devOtp
          ? `OTP (dev): ${devOtp} — Enter this code below.`
          : `OTP sent to ${phoneNumber}. Enter the code below.`
      );
      toast.success(devOtp ? `OTP (dev): ${devOtp}` : `OTP sent to ${phoneNumber}`);
      setOtp("");
    } catch (err) {
      setLocalError(err.message || "Failed to send OTP");
      toast.error(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLocalError("");
    setLocalSuccess("");
    if (!otp || otp.length !== 6) {
      setLocalError("Please enter a valid 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const result = await phoneLogin(phoneNumber, otp);
      const user = result?.user;
      const role = (user?.role || "").toLowerCase();

      if (!isPartnerRole(role)) {
        await logout?.();
        setLocalError(
          "This login is for vendors, garages, and partners only. Please use customer login or sign up as a partner."
        );
        toast.error("No partner account found for this number.");
        return;
      }

      toast.success("Signed in successfully");
      const returnTo = getPostLoginPath(user, { from: location.state?.from });
      if (role === "superadmin" || role === "admin") {
        navigate("/admin/dashboard", { replace: true });
      } else {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      setLocalError(err.message || "Invalid OTP or login failed");
      toast.error(err.message || "Invalid OTP or login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginPageWrapper className="auth-light-page">
      <div className="login-container">
        <div className="left-panel">
          <div className="form-wrapper">
            <div className="logo-section">
              <img loading="lazy" src="/sparepe.jpeg" alt="Sparepe" className="logo-image" />
            </div>

            <h1 className="form-title">Partner Login</h1>
            <p className="welcome-text">
              Login with your mobile number — for vendors, garages, mechanics &amp; delivery partners
            </p>

            {localError && <div className="error-box">{localError}</div>}

            {localSuccess && <div className="success-box">{localSuccess}</div>}

            <form className="login-form" onSubmit={otpSent ? handleVerifyOTP : handleSendOTP}>
              <div className="input-group">
                <label className="input-label">Phone Number</label>
                <div className="phone-input-wrapper">
                  <div className="country-code-selector">
                    <span className="country-code">+91</span>
                    <FaChevronDown className="dropdown-icon" />
                  </div>
                  <input
                    type="tel"
                    className="input-field phone-input"
                    placeholder="Enter Phone No."
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    required
                    maxLength="10"
                    disabled={otpSent}
                  />
                </div>
              </div>

              {otpSent && (
                <>
                  <div className="input-group">
                    <label className="input-label">Enter OTP</label>
                    <input
                      type="text"
                      className="input-field otp-input"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={handleOtpChange}
                      required
                      maxLength="6"
                      autoFocus
                    />
                  </div>
                  <div className="otp-actions">
                    <span
                      className="change-number-link"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp("");
                        setLocalSuccess("");
                        setLocalError("");
                      }}
                    >
                      Change number
                    </span>
                  </div>
                </>
              )}

              <button type="submit" className="login-btn" disabled={loading}>
                {loading
                  ? otpSent
                    ? "Verifying..."
                    : "Sending..."
                  : otpSent
                    ? "Verify OTP"
                    : "Get OTP"}
              </button>
            </form>

            <div className="signup-link-section">
              <span className="signup-text">Don&apos;t have an account? </span>
              <span
                className="signup-link"
                role="button"
                tabIndex={0}
                onClick={() => goTo("/signup")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") goTo("/signup");
                }}
              >
                Register as a vendor
              </span>
            </div>

            <div className="alt-login-link-section">
              <span className="alt-login-text">Shopping for parts? </span>
              <span
                className="alt-login-link"
                role="button"
                tabIndex={0}
                onClick={() => goTo("/login")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") goTo("/login");
                }}
              >
                Login as a customer
              </span>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <img
            loading="lazy"
            src={promoImage}
            alt="Partner workspace"
            className="promo-image-full"
          />
        </div>
      </div>
    </LoginPageWrapper>
  );
};

export default PartnerLogin;
